import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { daysBetween, todayPerth } from '@pti/domain';
import { api } from '../api';
import { useMe } from '../hooks';
import { fmtDate } from '../format';
import type { DocSummary, Run } from '../types';
import { ErrorNote, Icon, Loading, Modal, Pill, Section, TASK_TONE, toast } from '../components/ui';
import { DocTable } from './Documents';
import { taskLabel } from './Checklists';

/** New starter / leaver portal: their own to-dos and what PTI is doing, limited to tasks marked visible. */
export function MyOnboarding() {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery({ queryKey: ['my-checklist'], queryFn: () => api.get<Run | null>('/api/me/checklist') });
  const tick = useMutation({
    mutationFn: ({ run, task, done }: { run: number; task: number; done: boolean }) => api.send<Run>('POST', `/api/checklists/${run}/tasks/${task}`, { done }),
    onSuccess: (r) => qc.setQueryData(['my-checklist'], r), onError: (e) => toast((e as Error).message),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const r = q.data;
  const name = me.data?.person?.givenName ?? me.data?.user.name.split(' ')[0];
  if (!r) return (
    <div className="page"><div className="page-h"><div><p className="eyebrow">My PTI</p><h1>Hi {name}</h1><p className="lede">You have no onboarding or offboarding checklist right now.</p></div></div>
      <Link className="link" to="/me/documents">Go to My documents <Icon n="arrow" s={14} /></Link></div>
  );
  const onb = r.kind === 'onboarding';
  const mine = r.tasks.filter((t) => t.owner === 'New starter' || t.owner === 'Leaver');
  const others = r.tasks.filter((t) => !mine.includes(t));
  const key = onb ? r.anchors.start : r.anchors.last;
  const days = key ? daysBetween(todayPerth(), key) : null;
  return (
    <div className="page">
      <div className="welcome">
        <div>
          <p className="eyebrow">{onb ? 'Welcome to PTI' : 'Leaving PTI'}</p>
          <h1>Hi {name}{days != null && days > 0 ? (onb ? `, you start in ${days} day${days === 1 ? '' : 's'}` : `, your last day is in ${days} day${days === 1 ? '' : 's'}`) : ''}</h1>
          <p className="lede">{onb ? `${key ? fmtDate(key) + ' at' : 'At'} our Landsdale office, 5/15 Kalinga Way. Here is what to do, and what we are preparing for you.` : 'Here is what happens between now and your last day.'}</p>
        </div>
        {days != null && days > 0 && <div className="countdown"><span className="num">{days}</span><span>days to go</span></div>}
      </div>
      <Section title="Your to-do list" aside={<Pill tone={mine.every((t) => t.state === 'done') ? 'ok' : 'accent'}>{mine.filter((t) => t.state === 'done').length} of {mine.length} done</Pill>}>
        {mine.length === 0 ? <p className="empty">Nothing for you to do yet.</p> : (
          <ul className="chk tasks">
            {mine.map((t) => (
              <li key={t.id} className={t.state === 'done' ? 'is-done' : ''}>
                <input type="checkbox" id={`me-${t.id}`} checked={t.state === 'done'} disabled={tick.isPending} onChange={(e) => tick.mutate({ run: r.id, task: t.id, done: e.target.checked })} />
                <label htmlFor={`me-${t.id}`} className="chk-t"><strong>{t.title}</strong>{t.docTemplateKey && <span className="small dim">Opens in <Link to="/me/documents">My documents</Link></span>}</label>
                <span className="chk-s"><Pill tone={TASK_TONE[t.state]}>{t.state === 'done' ? 'Done' : `By ${fmtDate(t.dueDate)}`}</Pill></span>
              </li>
            ))}
          </ul>
        )}
        <Link className="link" to="/me/documents">Go to My documents <Icon n="arrow" s={14} /></Link>
      </Section>
      <Section title="What happens next">
        {['Before day 1', 'Day 1', 'First two weeks', 'Probation', 'On notice', 'Before last day', 'Last day', 'After leaving'].map((ph) => {
          const rows = others.filter((t) => t.phase === ph);
          if (!rows.length) return null;
          return (
            <div key={ph} className="chk-group"><h4>{ph}</h4>
              <ul className="timeline-list">{rows.map((t) => <li key={t.id} className={t.state === 'done' ? 'done' : ''}><span className="tl-date num">{fmtDate(t.dueDate)}</span><span><strong>{t.title}</strong><span className="small dim block">{t.owner === 'System' ? 'Automatic' : `with the ${t.owner}`}{t.state === 'done' ? ' · done' : ''}</span></span></li>)}</ul>
            </div>
          );
        })}
      </Section>
    </div>
  );
}

export function MyDocuments() {
  const qc = useQueryClient();
  const me = useMe();
  const [uploading, setUploading] = useState(false);
  const docs = useQuery({ queryKey: ['my-docs'], queryFn: () => api.get<DocSummary[]>('/api/me/documents') });
  const toSign = useQuery({ queryKey: ['to-sign'], queryFn: () => api.get<DocSummary[]>('/api/me/to-sign') });
  if (docs.isLoading || toSign.isLoading) return <Loading />;
  if (docs.error) return <ErrorNote error={docs.error} />;
  const waiting = toSign.data ?? [];
  const lib = (docs.data ?? []).filter((d) => !waiting.some((w) => w.id === d.id));
  return (
    <div className="page">
      <div className="page-h"><div><p className="eyebrow">My PTI</p><h1>My documents</h1><p className="lede">Everything you have signed with PTI, and anything waiting for you.</p></div></div>
      <Section title="Waiting for your signature" aside={waiting.length ? <Pill tone="accent">{waiting.length}</Pill> : undefined}>
        {waiting.length === 0 ? <p className="empty">Nothing to sign. You are up to date.</p> : (
          <ul className="todo">{waiting.map((d) => (
            <li key={d.id}><Icon n="doc" s={20} /><span><strong>{d.title}</strong><span className="small dim block">{d.personId !== me.data?.person?.id ? `${d.personName} · ` : ''}Sent {fmtDate(d.sentAt)}{d.dueDate ? ` · please sign by ${fmtDate(d.dueDate)}` : ''}</span></span>
              <Link className="btn primary" to={`/documents/${d.id}`}>Review and sign</Link></li>
          ))}</ul>
        )}
      </Section>
      <Section title="My library" aside={me.data?.person ? <button className="btn" onClick={() => setUploading(true)}>Upload a certificate</button> : undefined}>
        <DocTable docs={lib} />
        <p className="small dim">You can open and download anything you have signed, for as long as PTI holds it.</p>
      </Section>
      {uploading && <UploadCert onClose={() => setUploading(false)} onDone={() => { void qc.invalidateQueries({ queryKey: ['my-docs'] }); toast('Uploaded — the RTO Manager will verify it'); setUploading(false); }} />}
    </div>
  );
}

function UploadCert({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const m = useMutation({ mutationFn: () => { const fd = new FormData(); fd.append('title', title); fd.append('file', file!); return api.upload('/api/me/documents/upload', fd); }, onSuccess: onDone });
  return (
    <Modal title="Upload a certificate or evidence" onClose={onClose}>
      <label htmlFor="cert-in" className={`drop${file ? ' has' : ''}`}><Icon n="doc" s={28} />{file ? <strong>{file.name}</strong> : <><strong>Choose a PDF or photo</strong><span className="small dim">Qualification, statement of attainment, currency evidence</span></>}
        <input id="cert-in" type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, '')); }} /></label>
      <label className="field" htmlFor="cert-title"><span>What is it?</span><input id="cert-title" className="txt" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      {m.error && <ErrorNote error={m.error} />}
      <div className="row gap"><button className="btn primary" disabled={!file || !title || m.isPending} onClick={() => m.mutate()}>Upload</button><button className="btn" onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
